import { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AuthContext } from '@@/contexts/AuthContext';
import {
  InsightsApiClient,
  GetLambdaMetricsRequest,
  GetLambdaMetricsResponse,
  BaseRequestPayload
} from '../../api/api-client';
import { ResultValue } from '@@/types/ApiResult';

export const useLambdaMetrics = (parameters: Omit<GetLambdaMetricsRequest, 'instanceId' | 'region'>) => {
  const { instanceData } = useContext(AuthContext);

  const fetchMetrics = async (): Promise<GetLambdaMetricsResponse> => {
    if (!instanceData) {
      throw new Error('AppContext and AuthContext must be initialized.');
    }

    const client = new InsightsApiClient();
    const payload: BaseRequestPayload & GetLambdaMetricsRequest = {
      ...parameters,
      instanceId: instanceData.instanceId,
      region: instanceData.region
    };

    const result = await client.getLambdaMetrics(payload);

    if (!ResultValue.ok(result)) {
      throw new Error(result?.message || 'Failed to fetch lambda metrics');
    }

    return result.data;
  };

  const {
    data,
    isLoading,
    error: apiError,
    refetch,
    isRefetching
  } = useQuery<GetLambdaMetricsResponse, Error>({
    queryKey: [
      'lambda-metrics',
      instanceData?.instanceId,
      parameters.functionName,
      parameters.startTime,
      parameters.endTime
    ],
    queryFn: fetchMetrics,
    enabled: !!(instanceData?.instanceId && parameters.functionName && parameters.startTime && parameters.endTime),
    refetchOnWindowFocus: false,
    staleTime: 60000,
    retry: 1
  });

  return {
    data,
    isLoading,
    isRefetching,
    error: apiError?.message ?? null,
    refetch
  };
};
